// ═══════════════════════════════════════════════════════════
// 搜索逻辑 — 执行搜索 + 渲染结果卡片
// ═══════════════════════════════════════════════════════════

import { TIMEOUT, LIMITS } from '../config/constants.js';
import { esc, safeId, inferTag, extractRating } from '../lib/utils.js';
import { callApi } from '../lib/http.js';
import { showLoading, hideLoading } from '../ui/loading.js';
import { showToast } from '../ui/toast.js';

export function initSearch() {
    const searchInput = document.getElementById('searchInput');
    if (searchInput) {
        searchInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') executeSearch();
        });
    }

    const maxLen = LIMITS.MAX_QUERY_LENGTH;
    if (searchInput && !searchInput.hasAttribute('maxlength')) {
        searchInput.setAttribute('maxlength', maxLen);
    }
}

export async function executeSearch() {
    const searchInput = document.getElementById('searchInput');
    if (!searchInput) return;

    const query = searchInput.value.trim();
    if (!query) {
        showToast('请输入搜索内容', 'info');
        return;
    }

    showLoading();

    const source = window.getCurrentSource ? window.getCurrentSource() : 'hnzy';
    const customUrl = window.getCustomApiUrl ? window.getCustomApiUrl() : '';

    const params = { wd: query };
    if (source === 'custom') {
        params.customApi = customUrl;
    } else {
        params.source = source;
    }

    try {
        const data = await callApi('api/search', params, { timeout: TIMEOUT.SEARCH });

        if (data.code === 400) {
            showToast(data.msg || '搜索失败，请检查网络连接或更换数据源', 'error');
            return;
        }

        // 保存搜索历史
        saveSearchHistoryItem(query);

        // 显示结果区域
        switchToResultsLayout();

        const resultsDiv = document.getElementById('results');
        if (!resultsDiv) return;

        if (!data.list || data.list.length === 0) {
            resultsDiv.innerHTML = '<div class="col-span-full text-center text-gray-400 py-8">没有找到相关内容</div>';
            document.getElementById('resultsCount').textContent = '';
            return;
        }

        document.getElementById('resultsCount').textContent =
            `找到 ${data.total || data.list.length} 个结果`;

        // Sort results: exact match > rating > year > episode count
        const sortedList = [...data.list].sort((a, b) => {
            const aExact = (a.vod_name || '').includes(query) ? 1 : 0;
            const bExact = (b.vod_name || '').includes(query) ? 1 : 0;
            if (aExact !== bExact) return bExact - aExact;

            const aRating = parseFloat(a.vod_douban_score) || parseFloat(a.vod_score) || 0;
            const bRating = parseFloat(b.vod_douban_score) || parseFloat(b.vod_score) || 0;
            if (Math.abs(aRating - bRating) > 0.1) return bRating - aRating;

            const aYear = parseInt(a.vod_year) || 0;
            const bYear = parseInt(b.vod_year) || 0;
            if (aYear !== bYear) return bYear - aYear;

            const aEp = parseInt(a.vod_total) || 0;
            const bEp = parseInt(b.vod_total) || 0;
            return bEp - aEp;
        });

        resultsDiv.innerHTML = sortedList.map(item => {
            const id = item.vod_id ? item.vod_id.toString().replace(/[^\w-]/g, '') : '';
            const name = esc(item.vod_name);
            const picUrl = esc(item.vod_pic);
            const typeName = esc(item.type_name);
            const year = esc(item.vod_year);
            const area = esc(item.vod_area);
            const remarks = esc(item.vod_remarks);
            const actor = esc(item.vod_actor);
            const rating = extractRating(item);
            const tag = inferTag(item.vod_remarks);
            const episodeCount = parseInt(item.vod_total) || 0;
            const actorShort = actor ? actor.split(',').slice(0, 3).join(' · ') : '';

            return `
                <div class="movie-card" data-id="${id}" data-name="${name.replace(/"/g, '&quot;')}">
                    <div class="poster-wrap">
                        ${picUrl
                            ? `<img src="${picUrl}" alt="" loading="lazy" referrerpolicy="no-referrer"
                                 onerror="this.outerHTML='<div class=&quot;no-poster&quot;>🎬</div>'" />`
                            : `<div class="no-poster">${typeName && typeName.includes('动漫') ? '🎨' : typeName && typeName.includes('综艺') ? '🎪' : '🎬'}</div>`
                        }
                    </div>
                    <div class="info">
                        <div class="title">${name}${episodeCount > 1 ? ` <span class="ep-count">共${episodeCount}集</span>` : ''}</div>
                        <div class="meta-row">
                            ${typeName ? `<span>${typeName}</span>` : ''}
                            ${typeName && year ? '<span class="dot"></span>' : ''}
                            ${year ? `<span>${year}</span>` : ''}
                            ${(typeName || year) && area ? '<span class="dot"></span>' : ''}
                            ${area ? `<span>${area}</span>` : ''}
                            ${rating ? `<span class="dot"></span><span class="rating">⭐ ${rating.val}</span>` : ''}
                            ${tag ? `<span class="badge ${tag.cls}">${tag.text}</span>` : ''}
                        </div>
                        ${actorShort ? `<div class="desc-row">${actorShort}</div>` : (remarks ? `<div class="desc-row">${remarks}</div>` : '')}
                    </div>
                </div>`;
        }).join('');

        // 绑定卡片点击事件
        resultsDiv.querySelectorAll('.movie-card').forEach(card => {
            card.addEventListener('click', () => {
                const id = card.dataset.id;
                const name = card.dataset.name;
                if (typeof window.showVideoDetails === 'function') {
                    window.showVideoDetails(id, name);
                }
            });
        });

    } catch (error) {
        console.error('搜索错误:', error);
        showToast(error.message || '搜索请求失败，请稍后重试', 'error');
    } finally {
        hideLoading();
    }
}

function switchToResultsLayout() {
    document.getElementById('searchArea').classList.add('compact');
    document.getElementById('resultsArea').classList.remove('hidden');
}

function saveSearchHistoryItem(query) {
    if (!query || !query.trim()) return;
    // 委派给 history 模块
    if (typeof window.addSearchHistory === 'function') {
        window.addSearchHistory(query);
    }
}

// 全局暴露（HTML onclick 兼容）
window.search = executeSearch;
